import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import type { ReactNode } from "react";
import { DashboardErrorBoundary, SectionErrorBoundary } from "./components/layout/DashboardErrorBoundary.js";
import { RequireAuth } from "./components/auth/RequireAuth";
import { RequirePlatformAdmin } from "./components/auth/RequirePlatformAdmin";
import { RequireTenantApp } from "./components/auth/RequireTenantApp";
import { useAuth } from "./context/AuthContext";
import { useTenantFeatureFlags, useTenantBetFlags } from "./lib/tenant-feature-flags.js";
import type { ExperimentalBetKey } from "@identity-armour/shared/experimental-bets/completeness-gate";
import { AdminLayout } from "./components/layout/AdminLayout";
import { AppLayout } from "./components/layout/AppLayout";
import { SkipNavLink } from "./components/layout/SkipNavLink.js";
const BootstrapAdminPage = lazy(() => import("./pages/BootstrapAdminPage").then((m) => ({ default: m.BootstrapAdminPage })));
const IdentityDetailPage = lazy(() => import("./pages/IdentityDetailPage").then((m) => ({ default: m.IdentityDetailPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const AuditPage = lazy(() => import("./pages/AuditPage").then((m) => ({ default: m.AuditPage })));
const AccessDenialsPage = lazy(() =>
  import("./pages/AccessDenialsPage").then((m) => ({ default: m.AccessDenialsPage })),
);
const AwsMonitoringPage = lazy(() => import("./pages/AwsMonitoringPage").then((m) => ({ default: m.AwsMonitoringPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPageNew").then((m) => ({ default: m.DashboardPageNew })));
const PrivilegedAccessPage = lazy(() => import("./pages/PrivilegedAccessPage").then((m) => ({ default: m.PrivilegedAccessPage })));
const IdentitiesPage = lazy(() => import("./pages/IdentitiesPageNew").then((m) => ({ default: m.IdentitiesPageNew })));
const IdentityProvidersPage = lazy(() => import("./pages/IdentityProvidersPage").then((m) => ({ default: m.IdentityProvidersPage })));
const IdpDiagnosticsPage = lazy(() => import("./pages/IdpDiagnosticsPage").then((m) => ({ default: m.IdpDiagnosticsPage })));
const IdpSyncPage = lazy(() => import("./pages/IdpSyncPage").then((m) => ({ default: m.IdpSyncPage })));
const IncidentsPage = lazy(() => import("./pages/IncidentsPage").then((m) => ({ default: m.IncidentsPage })));
const IngestionToolsPage = lazy(() => import("./pages/IngestionToolsPage").then((m) => ({ default: m.IngestionToolsPage })));
const SessionCaptureConfigPage = lazy(() => import("./pages/SessionCaptureConfigPage").then((m) => ({ default: m.SessionCaptureConfigPage })));
const OidcProviderConfigPage = lazy(() => import("./pages/OidcProviderConfigPage").then((m) => ({ default: m.OidcProviderConfigPage })));
const OidcClientsPage = lazy(() => import("./pages/OidcClientsPage").then((m) => ({ default: m.OidcClientsPage })));
const OidcRevocationDrillPage = lazy(() => import("./pages/OidcRevocationDrillPage").then((m) => ({ default: m.OidcRevocationDrillPage })));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage").then((m) => ({ default: m.IntegrationsPage })));
const CopilotPage = lazy(() => import("./pages/CopilotPage.js").then((m) => ({ default: m.CopilotPage })));
const MarketplacePage = lazy(() => import("./pages/MarketplacePage.js").then((m) => ({ default: m.MarketplacePage })));
const MachineIdentitiesPage = lazy(() => import("./pages/MachineIdentitiesPage").then((m) => ({ default: m.MachineIdentitiesPage })));
const OnboardingHubPage = lazy(() => import("./pages/OnboardingHubPage").then((m) => ({ default: m.OnboardingHubPage })));
const ShadowModePage = lazy(() => import("./pages/ShadowModePage").then((m) => ({ default: m.ShadowModePage })));
const OperationsPage = lazy(() => import("./pages/OperationsPage").then((m) => ({ default: m.OperationsPage })));
const PoliciesPage = lazy(() => import("./pages/PoliciesPage").then((m) => ({ default: m.PoliciesPage })));
const PolicySimulationPage = lazy(() => import("./pages/PolicySimulationPage").then((m) => ({ default: m.PolicySimulationPage })));
const SharedSignalsPage = lazy(() => import("./pages/SharedSignalsPage").then((m) => ({ default: m.SharedSignalsPage })));
const ProtectedApplicationsPage = lazy(() => import("./pages/ProtectedApplicationsPage").then((m) => ({ default: m.ProtectedApplicationsPage })));
const TrustOverviewPage = lazy(() => import("./pages/TrustOverviewPage").then((m) => ({ default: m.TrustOverviewPage })));
const RevocationReceiptsPage = lazy(() => import("./pages/RevocationReceiptsPage").then((m) => ({ default: m.RevocationReceiptsPage })));
const PredictivePruningPage = lazy(() => import("./pages/PredictivePruningPage").then((m) => ({ default: m.PredictivePruningPage })));
const TrustCenterPage = lazy(() => import("./pages/TrustCenterPage").then((m) => ({ default: m.TrustCenterPage })));
const TrustSlaPage = lazy(() => import("./pages/TrustSlaPage").then((m) => ({ default: m.TrustSlaPage })));
const NhiInventoryPage = lazy(() => import("./pages/NhiInventoryPage").then((m) => ({ default: m.NhiInventoryPage })));
const UnifiedAssetsPage = lazy(() => import("./pages/UnifiedAssetsPage").then((m) => ({ default: m.UnifiedAssetsPage })));
const AttackPathWhatIfPage = lazy(() => import("./pages/AttackPathWhatIfPage").then((m) => ({ default: m.AttackPathWhatIfPage })));
const BrowserAccessPage = lazy(() => import("./pages/BrowserAccessPage").then((m) => ({ default: m.BrowserAccessPage })));
const AgentIntentsPage = lazy(() => import("./pages/AgentIntentsPage").then((m) => ({ default: m.AgentIntentsPage })));
const AgentRegistryPage = lazy(() => import("./pages/AgentRegistryPage").then((m) => ({ default: m.AgentRegistryPage })));
const AgentControlCoveragePage = lazy(() => import("./pages/AgentControlCoveragePage").then((m) => ({ default: m.AgentControlCoveragePage })));
const AgentApprovalsPage = lazy(() => import("./pages/AgentApprovalsPage").then((m) => ({ default: m.AgentApprovalsPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const PilotSuccessPage = lazy(() => import("./pages/PilotSuccessPage").then((m) => ({ default: m.PilotSuccessPage })));
const AccessReviewsPage = lazy(() => import("./pages/AccessReviewsPage").then((m) => ({ default: m.AccessReviewsPage })));
const AccessReviewCampaignPage = lazy(() => import("./pages/AccessReviewCampaignPage").then((m) => ({ default: m.AccessReviewCampaignPage })));
const ScimPage = lazy(() => import("./pages/ScimPage").then((m) => ({ default: m.ScimPage })));
const RolesPage = lazy(() => import("./pages/RolesPage").then((m) => ({ default: m.RolesPage })));
const MySessionsPage = lazy(() => import("./pages/MySessionsPage").then((m) => ({ default: m.MySessionsPage })));
const SessionsPage = lazy(() => import("./pages/SessionsPage").then((m) => ({ default: m.SessionsPage })));
const SessionDetailPage = lazy(() => import("./pages/SessionDetailPage").then((m) => ({ default: m.SessionDetailPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const VerdiktaPage = lazy(() => import("./pages/VerdiktaPage").then((m) => ({ default: m.VerdiktaPage })));
const WebhookSubscriptionsPage = lazy(() => import("./pages/WebhookSubscriptionsPage").then((m) => ({ default: m.WebhookSubscriptionsPage })));
const AlertsPage = lazy(() => import("./pages/AlertsPage").then((m) => ({ default: m.AlertsPage })));
const PlaybooksPage = lazy(() => import("./pages/PlaybooksPage").then((m) => ({ default: m.PlaybooksPage })));
const ArmourGraphPage = lazy(() => import("./pages/ArmourGraphPage").then((m) => ({ default: m.ArmourGraphPage })));
const DecoySecretsPage = lazy(() => import("./pages/DecoySecretsPage").then((m) => ({ default: m.DecoySecretsPage })));
const AiAgentHubPage = lazy(() => import("./pages/AiAgentHubPage").then((m) => ({ default: m.AiAgentHubPage })));
const VaultSecretsPage = lazy(() => import("./pages/VaultSecretsPage").then((m) => ({ default: m.VaultSecretsPage })));
const SecretLeasesPage = lazy(() => import("./pages/SecretLeasesPage").then((m) => ({ default: m.SecretLeasesPage })));
const CiemRecommendationsPage = lazy(() => import("./pages/CiemRecommendationsPage").then((m) => ({ default: m.CiemRecommendationsPage })));
const NhiLifecyclePage = lazy(() => import("./pages/NhiLifecyclePage").then((m) => ({ default: m.NhiLifecyclePage })));
const NhiPosturePage = lazy(() => import("./pages/NhiPosturePage").then((m) => ({ default: m.NhiPosturePage })));
const AgentDelegationPage = lazy(() => import("./pages/AgentDelegationPage").then((m) => ({ default: m.AgentDelegationPage })));
const McpGatewayPage = lazy(() => import("./pages/McpGatewayPage").then((m) => ({ default: m.McpGatewayPage })));
const AgentSessionsPage = lazy(() => import("./pages/AgentSessionsPage").then((m) => ({ default: m.AgentSessionsPage })));
const CiemLifecyclePage = lazy(() => import("./pages/CiemLifecyclePage").then((m) => ({ default: m.CiemLifecyclePage })));
const SiemSinksPage = lazy(() => import("./pages/SiemSinksPage").then((m) => ({ default: m.SiemSinksPage })));
const DsrPage = lazy(() => import("./pages/DsrPage").then((m) => ({ default: m.DsrPage })));
const ThreatIntelPage = lazy(() => import("./pages/ThreatIntelPage").then((m) => ({ default: m.ThreatIntelPage })));
const AgenticSocPage = lazy(() => import("./pages/AgenticSocPage").then((m) => ({ default: m.AgenticSocPage })));
const ImpossibleTravelPage = lazy(() => import("./pages/ImpossibleTravelPage").then((m) => ({ default: m.ImpossibleTravelPage })));
const MfaChallengesPage = lazy(() => import("./pages/MfaChallengesPage").then((m) => ({ default: m.MfaChallengesPage })));
const RemoteAccessPage = lazy(() => import("./pages/RemoteAccessPage").then((m) => ({ default: m.RemoteAccessPage })));
const EndpointAgentsPage = lazy(() => import("./pages/EndpointAgentsPage").then((m) => ({ default: m.EndpointAgentsPage })));
const AdminOverviewPage = lazy(() => import("./pages/admin/AdminOverviewPage").then((m) => ({ default: m.AdminOverviewPage })));
const AdminProvisionOperatorPage = lazy(() => import("./pages/admin/AdminProvisionOperatorPage").then((m) => ({ default: m.AdminProvisionOperatorPage })));
const AdminTenantsPage = lazy(() => import("./pages/admin/AdminTenantsPage").then((m) => ({ default: m.AdminTenantsPage })));
const ProviderConsolePage = lazy(() => import("./pages/admin/ProviderConsolePage").then((m) => ({ default: m.ProviderConsolePage })));
const AdminDefaultsPage = lazy(() => import("./pages/admin/AdminDefaultsPage").then((m) => ({ default: m.AdminDefaultsPage })));
const AdminOperationsPage = lazy(() => import("./pages/admin/AdminOperationsPage").then((m) => ({ default: m.AdminOperationsPage })));
const AdminIncidentsPage = lazy(() => import("./pages/admin/AdminIncidentsPage").then((m) => ({ default: m.AdminIncidentsPage })));
const PolicyDriftPage = lazy(() => import("./pages/PolicyDriftPage").then((m) => ({ default: m.PolicyDriftPage })));
const CloudPolicyDriftPage = lazy(() => import("./pages/CloudPolicyDriftPage").then((m) => ({ default: m.CloudPolicyDriftPage })));
const SiemDlqPage = lazy(() => import("./pages/SiemDlqPage").then((m) => ({ default: m.SiemDlqPage })));
/** Shown while a lazily-loaded route chunk is being fetched. */
function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted" role="status" aria-live="polite">
      Loading…
    </div>
  );
}

export function AwsMonitoringRouteGuard() {
  const { user } = useAuth();
  const role = user?.role ?? null;
  const tenantExternalId = user?.tenantExternalId ?? null;
  const { loading, flags } = useTenantFeatureFlags({ role, tenantExternalId });

  if (loading) {
    return <div className="px-6 py-6 text-sm text-muted">Loading AWS monitoring…</div>;
  }

  if (flags.aws_monitoring !== true) {
    return <Navigate to="/operations" replace />;
  }

  return (
    <SectionErrorBoundary>
      <AwsMonitoringPage />
    </SectionErrorBoundary>
  );
}

/**
 * Gates the Pilot Success page behind the platform-granted `pilot_access`
 * feature flag. Tenants without pilot access are redirected to the dashboard.
 */
export function PilotSuccessRouteGuard() {
  const { user } = useAuth();
  const role = user?.role ?? null;
  const tenantExternalId = user?.tenantExternalId ?? null;
  const { loading, flags } = useTenantFeatureFlags({ role, tenantExternalId });

  if (loading) {
    return <div className="px-6 py-6 text-sm text-muted">Loading pilot success…</div>;
  }

  if (flags.pilot_access !== true) {
    return <Navigate to="/" replace />;
  }

  return (
    <SectionErrorBoundary>
      <PilotSuccessPage />
    </SectionErrorBoundary>
  );
}

/**
 * Sprint S6 — experimental-bets completeness gate (dashboard side).
 *
 * Wraps a bet's page and redirects to the dashboard root when the bet is not
 * enabled for the current tenant. Mirrors the API Gateway `requireExperimentalBet`
 * middleware so a tenant who manually navigates to a gated URL is still blocked.
 */
function ExperimentalBetRouteGuard({
  betKey,
  loadingLabel,
  children,
}: {
  betKey: ExperimentalBetKey;
  loadingLabel: string;
  children: ReactNode;
}) {
  const { user } = useAuth();
  const role = user?.role ?? null;
  const tenantExternalId = user?.tenantExternalId ?? null;
  const { loading, flags } = useTenantBetFlags({ role, tenantExternalId });

  if (loading) {
    return <div className="px-6 py-6 text-sm text-muted">{loadingLabel}</div>;
  }

  if (flags[betKey] !== true) {
    return <Navigate to="/" replace />;
  }

  return <SectionErrorBoundary>{children}</SectionErrorBoundary>;
}

/**
 * RFC-004 R5.3 — real redirect that preserves query params. Used to fold the
 * retired /config hubs into the consolidated /settings IA without breaking
 * bookmarks or deep links.
 */
function RedirectWithQuery({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

/** Forwards /elevations?grant=... to /privileged-access?tab=elevations&grant=... */
function ElevationsRedirect() {
  const sp = new URLSearchParams(window.location.search);
  const grant = sp.get("grant");
  const target = new URLSearchParams({ tab: "elevations" });
  if (grant) target.set("grant", grant);
  return <Navigate to={`/privileged-access?${target.toString()}`} replace />;
}

export default function App() {
  return (
    <DashboardErrorBoundary>
      <SkipNavLink />
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/setup-admin" element={<BootstrapAdminPage />} />

          <Route element={<RequireAuth />}>
            <Route path="admin" element={<RequirePlatformAdmin />}>
              <Route element={<AdminLayout />}>
                <Route
                  index
                  element={
                    <SectionErrorBoundary>
                      <AdminOverviewPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="organizations"
                  element={
                    <SectionErrorBoundary>
                      <AdminTenantsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="operators"
                  element={
                    <SectionErrorBoundary>
                      <AdminProvisionOperatorPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="provider"
                  element={
                    <SectionErrorBoundary>
                      <ProviderConsolePage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="defaults"
                  element={
                    <SectionErrorBoundary>
                      <AdminDefaultsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="operations"
                  element={
                    <SectionErrorBoundary>
                      <AdminOperationsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="incidents"
                  element={
                    <SectionErrorBoundary>
                      <AdminIncidentsPage />
                    </SectionErrorBoundary>
                  }
                />
              </Route>
            </Route>

            <Route path="/" element={<RequireTenantApp />}>
              <Route element={<AppLayout />}>
                <Route
                  index
                  element={
                    <SectionErrorBoundary>
                      <DashboardPage />
                    </SectionErrorBoundary>
                  }
                />
                {/* RFC-004 R5.3: the /config hub is folded into /settings. */}
                <Route path="config" element={<RedirectWithQuery to="/settings" />} />
                <Route
                  path="config/idp-sync"
                  element={
                    <SectionErrorBoundary>
                      <IdpSyncPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="config/scim"
                  element={
                    <SectionErrorBoundary>
                      <ScimPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="config/roles"
                  element={
                    <SectionErrorBoundary>
                      <RolesPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="config/diagnostics"
                  element={
                    <SectionErrorBoundary>
                      <IdpDiagnosticsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="config/ingestion"
                  element={
                    <SectionErrorBoundary>
                      <IngestionToolsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="config/session-capture"
                  element={
                    <SectionErrorBoundary>
                      <SessionCaptureConfigPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="config/oidc-provider"
                  element={
                    <SectionErrorBoundary>
                      <OidcProviderConfigPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="oidc-clients"
                  element={
                    <SectionErrorBoundary>
                      <OidcClientsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="oidc-revocation-drill"
                  element={
                    <SectionErrorBoundary>
                      <OidcRevocationDrillPage />
                    </SectionErrorBoundary>
                  }
                />
                {/* RFC-004 R5.3: the session-settings link hub is folded into
                    the session-capture page (its primary destination). */}
                <Route
                  path="config/session-settings"
                  element={<RedirectWithQuery to="/config/session-capture" />}
                />
                <Route
                  path="onboarding"
                  element={
                    <SectionErrorBoundary>
                      <OnboardingHubPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="shadow-mode"
                  element={
                    <SectionErrorBoundary>
                      <ShadowModePage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="onboarding-flow"
                  element={<Navigate to="/onboarding?tab=setup" replace />}
                />
                <Route
                  path="onboarding-wizard"
                  element={<Navigate to="/onboarding?tab=setup" replace />}
                />
                <Route
                  path="onboarding/checklist"
                  element={<Navigate to="/onboarding?tab=add-connector" replace />}
                />
                <Route
                  path="connectors"
                  element={<Navigate to="/onboarding?tab=connectors" replace />}
                />
                <Route
                  path="verdikta"
                  element={
                    <SectionErrorBoundary>
                      <VerdiktaPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="identities/workloads"
                  element={
                    <SectionErrorBoundary>
                      <MachineIdentitiesPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="identities/:identityId"
                  element={
                    <SectionErrorBoundary>
                      <IdentityDetailPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="identities"
                  element={
                    <SectionErrorBoundary>
                      <IdentitiesPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="sessions"
                  element={
                    <SectionErrorBoundary>
                      <SessionsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="sessions/:sessionId"
                  element={
                    <SectionErrorBoundary>
                      <SessionDetailPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="policies/simulate"
                  element={
                    <SectionErrorBoundary>
                      <PolicySimulationPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="protected-applications"
                  element={
                    <SectionErrorBoundary>
                      <ProtectedApplicationsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="shared-signals"
                  element={
                    <SectionErrorBoundary>
                      <SharedSignalsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="trust-overview"
                  element={
                    <SectionErrorBoundary>
                      <TrustOverviewPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="revocation-receipts"
                  element={
                    <SectionErrorBoundary>
                      <RevocationReceiptsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="predictive-pruning"
                  element={
                    <SectionErrorBoundary>
                      <PredictivePruningPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="trust-center"
                  element={
                    <SectionErrorBoundary>
                      <TrustCenterPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="trust-sla"
                  element={
                    <ExperimentalBetRouteGuard betKey="trust_sla" loadingLabel="Loading Trust SLAs…">
                      <TrustSlaPage />
                    </ExperimentalBetRouteGuard>
                  }
                />
                <Route
                  path="attack-path-whatif"
                  element={
                    <SectionErrorBoundary>
                      <AttackPathWhatIfPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="browser-access"
                  element={
                    <ExperimentalBetRouteGuard betKey="browser_native" loadingLabel="Loading Browser access…">
                      <BrowserAccessPage />
                    </ExperimentalBetRouteGuard>
                  }
                />
                <Route
                  path="assets"
                  element={
                    <SectionErrorBoundary>
                      <UnifiedAssetsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="nhi-inventory"
                  element={
                    <SectionErrorBoundary>
                      <NhiInventoryPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="nhi-lifecycle"
                  element={
                    <SectionErrorBoundary>
                      <NhiLifecyclePage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="nhi-posture"
                  element={
                    <SectionErrorBoundary>
                      <NhiPosturePage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agent-delegation"
                  element={
                    <SectionErrorBoundary>
                      <AgentDelegationPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="mcp-gateway"
                  element={
                    <SectionErrorBoundary>
                      <McpGatewayPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agent-sessions"
                  element={
                    <SectionErrorBoundary>
                      <AgentSessionsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agent-approvals"
                  element={
                    <SectionErrorBoundary>
                      <AgentApprovalsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agents"
                  element={
                    <SectionErrorBoundary>
                      <AgentRegistryPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agent-intents"
                  element={
                    <SectionErrorBoundary>
                      <AgentIntentsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agent-control-coverage"
                  element={
                    <SectionErrorBoundary>
                      <AgentControlCoveragePage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agent-delegation-graph"
                  element={<Navigate to="/agent-delegation?tab=graph" replace />}
                />
                <Route
                  path="policy-drift"
                  element={
                    <SectionErrorBoundary>
                      <PolicyDriftPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="cloud-policy-drift"
                  element={
                    <SectionErrorBoundary>
                      <CloudPolicyDriftPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="siem/dlq"
                  element={
                    <SectionErrorBoundary>
                      <SiemDlqPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="policies"
                  element={
                    <SectionErrorBoundary>
                      <PoliciesPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="privileged-access"
                  element={
                    <SectionErrorBoundary>
                      <PrivilegedAccessPage />
                    </SectionErrorBoundary>
                  }
                />
                {/* Backward-compatible alias: /elevations?grant=... → /privileged-access?tab=elevations&grant=... */}
                <Route
                  path="elevations"
                  element={<ElevationsRedirect />}
                />
                <Route
                  path="operations/aws"
                  element={<AwsMonitoringRouteGuard />}
                />
                <Route
                  path="operations"
                  element={
                    <SectionErrorBoundary>
                      <OperationsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="incidents"
                  element={
                    <SectionErrorBoundary>
                      <IncidentsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="agentic-soc"
                  element={
                    <SectionErrorBoundary>
                      <AgenticSocPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="reports"
                  element={
                    <SectionErrorBoundary>
                      <ReportsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route path="pilot-success" element={<PilotSuccessRouteGuard />} />
                <Route
                  path="access-reviews"
                  element={
                    <SectionErrorBoundary>
                      <AccessReviewsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="access-reviews/:campaignExternalId"
                  element={
                    <SectionErrorBoundary>
                      <AccessReviewCampaignPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="audit"
                  element={
                    <SectionErrorBoundary>
                      <AuditPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="access-denials"
                  element={
                    <SectionErrorBoundary>
                      <AccessDenialsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="integrations"
                  element={
                    <SectionErrorBoundary>
                      <IntegrationsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="copilot"
                  element={
                    <SectionErrorBoundary>
                      <CopilotPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="marketplace"
                  element={
                    <SectionErrorBoundary>
                      <MarketplacePage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="integrations/webhooks"
                  element={
                    <SectionErrorBoundary>
                      <WebhookSubscriptionsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="identity-providers"
                  element={
                    <SectionErrorBoundary>
                      <IdentityProvidersPage />
                    </SectionErrorBoundary>
                  }
                />
                {/* Backward-compatible route alias (older docs/bookmarks). */}
                <Route path="idp" element={<Navigate to="/identity-providers" replace />} />
                <Route
                  path="settings"
                  element={
                    <SectionErrorBoundary>
                      <SettingsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="dev/my-sessions"
                  element={
                    <SectionErrorBoundary>
                      <MySessionsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="alerts"
                  element={
                    <SectionErrorBoundary>
                      <AlertsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="playbooks"
                  element={
                    <SectionErrorBoundary>
                      <PlaybooksPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="recordings"
                  element={<Navigate to="/sessions?tab=recordings" replace />}
                />
                <Route
                  path="armour-graph"
                  element={
                    <SectionErrorBoundary>
                      <ArmourGraphPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="decoy-secrets"
                  element={
                    <SectionErrorBoundary>
                      <DecoySecretsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="ai-agents"
                  element={
                    <SectionErrorBoundary>
                      <AiAgentHubPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="vault-secrets"
                  element={
                    <SectionErrorBoundary>
                      <VaultSecretsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="secret-leases"
                  element={
                    <SectionErrorBoundary>
                      <SecretLeasesPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="ciem"
                  element={
                    <SectionErrorBoundary>
                      <CiemRecommendationsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="ciem-lifecycle"
                  element={
                    <SectionErrorBoundary>
                      <CiemLifecyclePage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="siem/sinks"
                  element={
                    <SectionErrorBoundary>
                      <SiemSinksPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="dsr"
                  element={
                    <SectionErrorBoundary>
                      <DsrPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="threat-intel"
                  element={
                    <ExperimentalBetRouteGuard betKey="federated_psi" loadingLabel="Loading Threat intelligence…">
                      <ThreatIntelPage />
                    </ExperimentalBetRouteGuard>
                  }
                />
                <Route
                  path="impossible-travel"
                  element={
                    <SectionErrorBoundary>
                      <ImpossibleTravelPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="mfa/challenges"
                  element={
                    <SectionErrorBoundary>
                      <MfaChallengesPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="remote-access"
                  element={
                    <SectionErrorBoundary>
                      <RemoteAccessPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route
                  path="endpoint-agents"
                  element={
                    <SectionErrorBoundary>
                      <EndpointAgentsPage />
                    </SectionErrorBoundary>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Route>
          </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </DashboardErrorBoundary>
  );
}